// 万屋（里から入る。銭で買う）。品物（材木・ガチャ券・錬成札）は買うたびに値上がりし、日付が変わると元の値に戻る。
// 道具（戦闘に持ち出すもの）は一度買えばずっと使える解放。主Lv が ITEM_UNLOCK に届くと店に並ぶ。回復薬は最初から持っている。
import type { ItemId } from '../types';
import { ITEM_IDS } from '../types';
import { CONFIG } from './config';
import { ITEM_UNLOCK } from './items';
import type { OpResult, Profile, Resources } from './profile';

export type ShopGoodId = 'zaimoku' | 'kuji' | 'fuse';
export const SHOP_GOOD_IDS: readonly ShopGoodId[] = ['zaimoku', 'kuji', 'fuse'];

export interface ShopGoodView {
  id: ShopGoodId;
  name: string;
  /** 1回に買える量 */
  amount: number;
  /** 今日、次に買うときの値段（銭） */
  price: number;
  /** 今日買った回数 */
  bought: number;
  /** 1日に買える回数（0 なら上限なし） */
  daily: number;
  soldOut: boolean;
}

export interface ShopItemView {
  id: ItemId;
  /** 解放の値段（銭）。回復薬は 0 */
  price: number;
  /** 店に並ぶ主Lv */
  unlockLv: number;
  owned: boolean;
  /** 主Lv が届いていて、まだ持っていない */
  available: boolean;
}

/** その日の購入数（日付が変わっていたら 0 から） */
const countToday = (p: Profile, dayKey: string, id: ShopGoodId) => (p.shop?.day === dayKey ? (p.shop.count?.[id] ?? 0) : 0);

/** n 回目（0 始まり）に買うときの値段：元の値 ×(1+rise)^n を100銭単位に */
export function goodPrice(id: ShopGoodId, n: number): number {
  const g = CONFIG.shop.goods[id];
  return Math.max(1, Math.round((g.price * Math.pow(1 + g.rise, n)) / 100) * 100);
}

export function shopGoods(p: Profile, dayKey: string): ShopGoodView[] {
  return SHOP_GOOD_IDS.map((id) => {
    const g = CONFIG.shop.goods[id];
    const bought = countToday(p, dayKey, id);
    return { id, name: g.name, amount: g.amount, price: goodPrice(id, bought), bought, daily: g.daily, soldOut: g.daily > 0 && bought >= g.daily };
  });
}

export function buyGood(p: Profile, res: Resources, dayKey: string, id: ShopGoodId): OpResult<{ id: ShopGoodId; amount: number; price: number; bought: number }> {
  const g = CONFIG.shop.goods[id as ShopGoodId];
  if (!g) return { ok: false, error: 'unknown_good' };
  const n = countToday(p, dayKey, id);
  if (g.daily > 0 && n >= g.daily) return { ok: false, error: 'daily_limit' };
  const price = goodPrice(id, n);
  if (res.zeni < price) return { ok: false, error: 'zeni' };
  res.zeni -= price;
  if (p.shop?.day !== dayKey) p.shop = { day: dayKey, count: {} };
  p.shop.count[id] = n + 1;
  if (id === 'zaimoku') res.zaimoku += g.amount;
  else if (id === 'kuji') res.kuji = (res.kuji ?? 0) + g.amount;
  else if (id === 'fuse') p.tickets.fuse = (p.tickets.fuse ?? 0) + g.amount;
  return { ok: true, id, amount: g.amount, price, bought: n + 1 };
}

/** 持っている道具（回復薬は最初から） */
export const ownsItem = (p: Profile, id: ItemId) => id === 'heal' || (p.ownedItems ?? []).includes(id);

export function shopItems(p: Profile): ShopItemView[] {
  return ITEM_IDS.map((id) => {
    const owned = ownsItem(p, id);
    return { id, price: CONFIG.shop.items[id] ?? 0, unlockLv: ITEM_UNLOCK[id], owned, available: !owned && p.level >= ITEM_UNLOCK[id] };
  });
}

export function buyItem(p: Profile, res: Resources, id: ItemId): OpResult<{ id: ItemId; price: number }> {
  if (!ITEM_IDS.includes(id)) return { ok: false, error: 'unknown_item' };
  if (ownsItem(p, id)) return { ok: false, error: 'owned' };
  if (p.level < ITEM_UNLOCK[id]) return { ok: false, error: 'item_level' };
  const price = CONFIG.shop.items[id] ?? 0;
  if (res.zeni < price) return { ok: false, error: 'zeni' };
  res.zeni -= price;
  p.ownedItems = [...(p.ownedItems ?? []), id];
  return { ok: true, id, price };
}
